/**
 * Quotes' search provider for the Z1 search slot (S6; G0 moved it out of SearchSlot.jsx).
 *
 * A search provider is { id, label, kind, search(term) -> rows, errorText, rowKey(row),
 * renderRow(row), href(row), seeAll? }. The slot asks the registry's providers in order
 * (modules/registry.js), shows each one's rows as a group, then the shared /api/search groups,
 * and its footer is every provider's seeAll in that order (then the slot's own "See all in
 * Customers", C6).
 *
 * Quotes come from the S1a list contract — GET /api/quotes?view=all&q&sort=date&dir=desc
 * &page=1&limit=8 — so a quote is found exactly as the list finds it (number, customer, …). A
 * row shows the number, the customer, the status (quoteStatusMap's column label and tone) and
 * the value; it opens /quotes/:id (push: list → record).
 */
import { FileText } from 'lucide-react';
import { requestJson } from '../../services/quotesApi';
import { listRequestUrl } from './useQuoteListData';
import { quoteStatusDisplay } from './quoteStatusMap';
import { formatCentsCurrency } from '../../utils/quoteFormat';

export const SLOT_QUOTE_LIMIT = 8;

export function slotQuotesUrl(term) {
  return listRequestUrl(
    { view: 'all', q: term, sort: 'date', dir: 'desc', page: 1 },
    { limit: SLOT_QUOTE_LIMIT }
  );
}

export const quoteSearchProvider = Object.freeze({
  id: 'quotes',
  label: 'Quotes',
  kind: 'quote',
  errorText: 'Quotes search unavailable',
  async search(term) {
    const body = await requestJson('GET', slotQuotesUrl(term));
    return Array.isArray(body?.quotations) ? body.quotations.slice(0, SLOT_QUOTE_LIMIT) : [];
  },
  rowKey: (q) => `quote-${q.id}`,
  href: (q) => `/quotes/${q.id}`,
  renderRow(q) {
    const status = quoteStatusDisplay(q.status);
    return (
      <>
        <FileText className="shell-search-icon" aria-hidden="true" />
        <span className="shell-search-main">
          <span className="shell-search-title">{q.quote_number || q.quotation_number || `Quote ${q.id}`}</span>
          <span className="shell-search-sub">{q.customer_name || 'No customer'}</span>
        </span>
        <span className="shell-search-status" style={{ '--shell-search-tone': `var(--status-${status.tone})` }}>
          <span className="shell-search-dot" aria-hidden="true" />
          {status.label}
        </span>
        <span className="shell-search-value">{formatCentsCurrency(q.total_cents)}</span>
      </>
    );
  },
  seeAll: Object.freeze({
    title: 'See all in Quotes',
    sub: (term) => `“${term}” in the quotes list`,
    href: (term) => `/quotes?view=all&q=${encodeURIComponent(term)}`
  })
});
